import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Target as TargetIcon, Edit2, Trash2, CheckCircle2, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';
import ConfirmDialog from '../components/ConfirmDialog';

const FILTER_TABS = [
  { value: 'active', label: 'Active' },
  { value: 'achieved', label: 'Achieved' },
  { value: 'all', label: 'All' },
];

const categoryBadgeClass = {
  revenue: 'bg-gold-light text-gold',
  product: 'bg-lavender-light text-lavender',
  brand: 'bg-amber-light text-amber',
  personal: 'bg-teal-light text-teal',
  relationships: 'bg-teal-light text-teal',
  learning: 'bg-sage-light text-sage',
};

function formatGoalDate(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).slice(0, 10);
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** PUT body for status toggles: include title + category so backends that still expect GoalCreate on PUT do not 422. */
function goalStatusUpdateBody(goal, status) {
  return {
    title: goal.title ?? '',
    category: goal.category || 'revenue',
    status,
  };
}

const GoalsPage = () => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [formModal, setFormModal] = useState(null);
  const [createModalNonce, setCreateModalNonce] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const openCreateModal = () => {
    setCreateModalNonce((n) => n + 1);
    setFormModal({ mode: 'create' });
  };

  const fetchGoals = useCallback(async () => {
    setLoading(true);
    try {
      const path =
        filter === 'all' ? '/api/goals' : `/api/goals?status=${encodeURIComponent(filter)}`;
      const data = await apiFetch(path);
      setGoals(data.goals || []);
    } catch (error) {
      console.error('Error fetching goals:', error);
      toast.error(error?.message || 'Could not load goals');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchGoals();
  }, [fetchGoals]);

  const performDelete = async () => {
    if (!deleteTarget) return;
    await apiFetch(`/api/goals/${deleteTarget.id}`, { method: 'DELETE' });
    toast.success('Goal removed');
    fetchGoals();
  };

  const emptyCopy =
    filter === 'all'
      ? { title: 'No goals yet', body: 'Create a goal and track steps to completion' }
      : filter === 'achieved'
        ? { title: 'Nothing achieved yet', body: 'Mark an active goal complete when you get there' }
        : { title: 'No active goals', body: 'Add a goal or check Achieved / All' };

  return (
    <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="goals-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-4xl font-bold text-text-primary mb-2">Goals & milestones</h1>
            <p className="text-text-secondary">
              Active = in progress · Achieved = done · Check off steps to update progress
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateModal}
            className="flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all shadow-sm"
            data-testid="new-goal-button"
          >
            <Plus size={20} />
            New goal
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {FILTER_TABS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setFilter(value)}
              className={`px-4 py-2 rounded-lg font-medium transition-all border-2 ${
                filter === value
                  ? 'bg-gold text-white border-gold'
                  : 'bg-bg-surface text-text-secondary border-border hover:border-gold'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-2 border-gold/30 border-t-gold rounded-full animate-spin mx-auto mb-4" />
          </div>
        ) : goals.length === 0 ? (
          <div className="text-center py-12 bg-bg-surface rounded-2xl border border-border">
            <div className="w-16 h-16 bg-sage-light rounded-full flex items-center justify-center mx-auto mb-4">
              <TargetIcon className="text-sage" size={32} />
            </div>
            <h3 className="font-display text-xl font-bold text-text-primary mb-2">{emptyCopy.title}</h3>
            <p className="text-text-secondary mb-6">{emptyCopy.body}</p>
            {filter === 'active' || filter === 'all' ? (
              <button
                type="button"
                onClick={openCreateModal}
                className="inline-flex items-center gap-2 px-6 py-3 bg-gold hover:bg-gold-muted text-white font-semibold rounded-xl transition-all"
              >
                <Plus size={20} />
                Create first goal
              </button>
            ) : null}
          </div>
        ) : (
          <div className="grid gap-6">
            {goals.map((goal) => (
              <GoalCard
                key={goal.id}
                goal={goal}
                onUpdate={fetchGoals}
                onEdit={() => setFormModal({ mode: 'edit', goal })}
                onDelete={() =>
                  setDeleteTarget({ id: goal.id, preview: goal.title })
                }
              />
            ))}
          </div>
        )}

        {formModal ? (
          <GoalFormModal
            key={formModal.mode === 'edit' ? formModal.goal.id : `create-${createModalNonce}`}
            mode={formModal.mode}
            goal={formModal.mode === 'edit' ? formModal.goal : null}
            onClose={() => setFormModal(null)}
            onSave={fetchGoals}
          />
        ) : null}

        <ConfirmDialog
          open={!!deleteTarget}
          onClose={() => setDeleteTarget(null)}
          title="Delete this goal?"
          message="Steps under this goal will be removed too. This can’t be undone."
          detail={deleteTarget?.preview}
          confirmLabel="Delete goal"
          onConfirm={performDelete}
        />
      </div>
    </div>
  );
};

const GoalCard = ({ goal, onUpdate, onEdit, onDelete }) => {
  const cat = (goal.category || 'revenue').toLowerCase();
  const badgeCls = categoryBadgeClass[cat] || categoryBadgeClass.revenue;
  const isActive = goal.status === 'active';
  const isAchieved = goal.status === 'achieved';
  const [stepBusy, setStepBusy] = useState(null);
  const [statusBusy, setStatusBusy] = useState(false);

  const completedSteps = goal.steps?.filter((s) => s.completed).length || 0;
  const totalSteps = goal.steps?.length || 0;
  const calculatedProgress =
    totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : goal.progress ?? 0;

  const toggleStep = async (step) => {
    const key = `${goal.id}:${step.id}`;
    setStepBusy(key);
    try {
      await apiFetch(
        `/api/goals/${goal.id}/steps/${step.id}?completed=${!step.completed}`,
        { method: 'PUT' }
      );
      onUpdate();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'Could not update step');
    } finally {
      setStepBusy(null);
    }
  };

  const markAchieved = async () => {
    setStatusBusy(true);
    try {
      await apiFetch(`/api/goals/${goal.id}`, {
        method: 'PUT',
        body: JSON.stringify(goalStatusUpdateBody(goal, 'achieved')),
      });
      toast.success('Marked as achieved');
      onUpdate();
    } catch (e) {
      toast.error(e?.message || 'Could not update goal');
    } finally {
      setStatusBusy(false);
    }
  };

  const reopenGoal = async () => {
    setStatusBusy(true);
    try {
      await apiFetch(`/api/goals/${goal.id}`, {
        method: 'PUT',
        body: JSON.stringify(goalStatusUpdateBody(goal, 'active')),
      });
      toast.success('Goal is active again');
      onUpdate();
    } catch (e) {
      toast.error(e?.message || 'Could not update goal');
    } finally {
      setStatusBusy(false);
    }
  };

  const targetLabel = formatGoalDate(goal.target_date);
  const startLabel = formatGoalDate(goal.start_date);

  return (
    <div className="bg-bg-surface rounded-2xl p-6 border border-border card-hover">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`px-3 py-1 text-xs font-semibold rounded-full capitalize ${badgeCls}`}>
              {cat}
            </span>
            <span
              className={`px-3 py-1 text-xs font-semibold rounded-full capitalize ${
                isAchieved ? 'bg-gold-light text-gold' : 'bg-sage-light text-sage'
              }`}
            >
              {isAchieved ? 'Achieved' : 'Active'}
            </span>
          </div>
          <h3 className="font-display text-2xl font-bold text-text-primary mb-2 break-words">{goal.title}</h3>
          {goal.why_it_matters ? (
            <p className="text-text-secondary italic mb-2">&ldquo;{goal.why_it_matters}&rdquo;</p>
          ) : null}
          {(startLabel || targetLabel) && (
            <p className="text-xs text-text-tertiary">
              {startLabel ? `Started ${startLabel}` : ''}
              {startLabel && targetLabel ? ' · ' : ''}
              {targetLabel ? `Target ${targetLabel}` : ''}
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {isActive ? (
            <button
              type="button"
              onClick={markAchieved}
              disabled={statusBusy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-sage text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              <CheckCircle2 size={16} />
              Mark achieved
            </button>
          ) : (
            <button
              type="button"
              onClick={reopenGoal}
              disabled={statusBusy}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-text-secondary text-sm font-medium hover:bg-gold-light hover:text-gold disabled:opacity-50"
            >
              <RotateCcw size={16} />
              Set active again
            </button>
          )}
          <button
            type="button"
            onClick={onEdit}
            className="p-2 rounded-lg border border-border text-text-secondary hover:bg-gold-light hover:text-gold"
            aria-label="Edit goal"
          >
            <Edit2 size={18} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="p-2 rounded-lg border border-border text-text-secondary hover:bg-error-light hover:text-error"
            aria-label="Delete goal"
          >
            <Trash2 size={18} />
          </button>
        </div>
      </div>

      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-text-primary">{calculatedProgress}% complete</span>
        </div>
        <div className="w-full bg-sage-light/30 rounded-full h-2">
          <div
            className="bg-sage rounded-full h-2 transition-all"
            style={{ width: `${Math.min(100, calculatedProgress)}%` }}
          />
        </div>
      </div>

      {goal.steps && goal.steps.length > 0 ? (
        <div className="space-y-2">
          <p className="text-xs font-medium text-text-secondary uppercase tracking-wide">Steps</p>
          {goal.steps.map((step) => {
            const busy = stepBusy === `${goal.id}:${step.id}`;
            return (
              <button
                key={step.id}
                type="button"
                disabled={busy || isAchieved}
                onClick={() => toggleStep(step)}
                className={`w-full flex items-center gap-3 text-left rounded-xl px-2 py-2 transition-colors ${
                  isAchieved ? 'opacity-60 cursor-not-allowed' : 'hover:bg-bg-surface-alt'
                }`}
              >
                <div
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                    step.completed ? 'bg-sage border-sage' : 'border-border'
                  }`}
                >
                  {step.completed ? (
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : null}
                </div>
                <span
                  className={`text-sm ${
                    step.completed ? 'text-text-tertiary line-through' : 'text-text-primary'
                  }`}
                >
                  {step.step_text}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <p className="text-sm text-text-tertiary">No steps — progress uses the bar above (e.g. after promoting from an idea).</p>
      )}
    </div>
  );
};

const GoalFormModal = ({ mode, goal, onClose, onSave }) => {
  const isEdit = mode === 'edit';
  const [formData, setFormData] = useState({
    title: '',
    why_it_matters: '',
    category: 'revenue',
    target_date: '',
    start_date: '',
    status: 'active',
    stepsText: '',
  });
  // For edit mode: track existing steps, which to remove, and new ones to add
  const [existingSteps, setExistingSteps] = useState([]);
  const [removeStepIds, setRemoveStepIds] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (goal) {
      setFormData({
        title: goal.title || '',
        why_it_matters: goal.why_it_matters || '',
        category: goal.category || 'revenue',
        target_date: goal.target_date ? String(goal.target_date).slice(0, 10) : '',
        start_date: goal.start_date ? String(goal.start_date).slice(0, 10) : '',
        status: goal.status === 'achieved' ? 'achieved' : 'active',
        stepsText: '',
      });
      setExistingSteps(goal.steps || []);
      setRemoveStepIds([]);
    }
  }, [goal]);

  const handleRemoveExistingStep = (stepId) => {
    setRemoveStepIds((prev) => [...prev, stepId]);
  };

  const handleRestoreStep = (stepId) => {
    setRemoveStepIds((prev) => prev.filter((id) => id !== stepId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const newStepLines = formData.stepsText
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean);
        await apiFetch(`/api/goals/${goal.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            title: formData.title,
            why_it_matters: formData.why_it_matters || null,
            category: formData.category,
            target_date: formData.target_date || null,
            start_date: formData.start_date || null,
            status: formData.status,
            new_steps: newStepLines.length > 0 ? newStepLines : undefined,
            remove_step_ids: removeStepIds.length > 0 ? removeStepIds : undefined,
          }),
        });
        toast.success('Goal updated');
      } else {
        const steps = formData.stepsText
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean);
        await apiFetch('/api/goals', {
          method: 'POST',
          body: JSON.stringify({
            title: formData.title,
            why_it_matters: formData.why_it_matters || undefined,
            category: formData.category,
            target_date: formData.target_date || undefined,
            start_date: formData.start_date || undefined,
            steps,
          }),
        });
        toast.success('Goal created');
      }
      onSave();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || 'Request failed');
    } finally {
      setSaving(false);
    }
  };

  const visibleExistingSteps = existingSteps.filter((s) => !removeStepIds.includes(s.id));
  const removedExistingSteps = existingSteps.filter((s) => removeStepIds.includes(s.id));

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-overlay-in" onClick={onClose}>
      <div
        className="bg-bg-surface rounded-2xl p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-modal-in custom-scrollbar"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-display text-3xl font-bold text-text-primary mb-6">
          {isEdit ? 'Edit goal' : 'New goal'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">What do you want to achieve?</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Launch my first product"
              required
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              data-testid="goal-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Why does it matter?</label>
            <textarea
              value={formData.why_it_matters}
              onChange={(e) => setFormData({ ...formData, why_it_matters: e.target.value })}
              placeholder="Because…"
              rows={3}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="revenue">Revenue</option>
                <option value="product">Product</option>
                <option value="brand">Brand</option>
                <option value="personal">Personal</option>
                <option value="relationships">Relationships</option>
                <option value="learning">Learning</option>
              </select>
            </div>
            {isEdit ? (
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
                >
                  <option value="active">Active</option>
                  <option value="achieved">Achieved</option>
                </select>
              </div>
            ) : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Start date</label>
              <input
                type="date"
                value={formData.start_date}
                onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Target date</label>
              <input
                type="date"
                value={formData.target_date}
                onChange={(e) => setFormData({ ...formData, target_date: e.target.value })}
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              />
            </div>
          </div>

          {/* Existing steps (edit mode) */}
          {isEdit && existingSteps.length > 0 ? (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">
                Current steps
              </label>
              <div className="space-y-2">
                {visibleExistingSteps.map((step) => (
                  <div
                    key={step.id}
                    className="flex items-center gap-3 px-3 py-2.5 bg-bg-surface-alt/50 rounded-xl border border-border group"
                  >
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                        step.completed ? 'bg-sage border-sage' : 'border-border'
                      }`}
                    >
                      {step.completed ? (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      ) : null}
                    </div>
                    <span className={`flex-1 text-sm ${step.completed ? 'text-text-tertiary line-through' : 'text-text-primary'}`}>
                      {step.step_text}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveExistingStep(step.id)}
                      className="p-1 rounded-lg text-text-tertiary hover:text-error hover:bg-error-light/50 transition-colors opacity-0 group-hover:opacity-100"
                      aria-label="Remove step"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                {removedExistingSteps.map((step) => (
                  <div
                    key={step.id}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-dashed border-error/30 bg-error-light/20"
                  >
                    <span className="flex-1 text-sm text-text-tertiary line-through">{step.step_text}</span>
                    <button
                      type="button"
                      onClick={() => handleRestoreStep(step.id)}
                      className="text-xs text-gold hover:text-gold-muted font-medium"
                    >
                      Undo
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {/* Add steps (both create and edit mode) */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              {isEdit ? 'Add new steps' : 'Steps'}{' '}
              <span className="text-text-tertiary font-normal">(optional, one per line)</span>
            </label>
            <textarea
              value={formData.stepsText}
              onChange={(e) => setFormData({ ...formData, stepsText: e.target.value })}
              placeholder="Ship MVP&#10;Get 10 users&#10;…"
              rows={4}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none font-mono text-sm"
            />
          </div>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-border text-text-secondary rounded-xl hover:border-gold transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.title.trim()}
              className="flex-1 py-3 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary/30 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all"
            >
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create goal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GoalsPage;
